import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Button,
    Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { getOneLink } from 'src/utils';
import LoadingIndicator from './LoadingIndicator';
import { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { ScrollContext, WasteDataContext } from 'src/context';
import { queryWasteMeasurementsForDataset, WasteMeasurementWithMaterial } from 'src/schema';
import DeleteMeasurementDialog from './DeleteMeasurementDialog';

interface Props {
    datasetId: string;
}

// Scroll to bottom when a new measurement is added
// Track initialization to avoid triggering on first render
const useArrayGrowth = <T,>(array: T[], enable: boolean, callback: () => void) => {
    const prevLengthRef = useRef(array.length);
    const isInitializedRef = useRef(false);

    useEffect(() => {
        if (enable) {
            if (isInitializedRef.current && array.length > prevLengthRef.current) {
                callback();
            }
            if (!isInitializedRef.current) {
                isInitializedRef.current = true;
            }
            prevLengthRef.current = array.length;
        }
    }, [array.length, callback, enable]);
};

export default function DataViewTable(props: Props) {
    const { datasetId } = props;
    const { data, isLoading, error } = queryWasteMeasurementsForDataset(datasetId);
    const { editMode, selectedRow, setSelectedRow } = useContext(WasteDataContext);
    const [loadingTimeout, setLoadingTimeout] = useState(false);

    useEffect(() => {
        if (!isLoading) return;

        const timer = setTimeout(() => {
            setLoadingTimeout(true);
        }, 5000);

        return () => {
            clearTimeout(timer);
            setLoadingTimeout(false);
        };
    }, [isLoading]);

    const handleClickRow = useCallback(
        (clickedRow: WasteMeasurementWithMaterial) => {
            if (!editMode) {
                return;
            }
            if (clickedRow.id === selectedRow?.id) {
                // deselect on second click
                setSelectedRow(null);
            } else {
                setSelectedRow(clickedRow);
            }
        },
        [editMode, selectedRow, setSelectedRow]
    );

    // Scroll to bottom when a new measurement is added
    const { scrollToBottom } = useContext(ScrollContext);
    const wasteMeasurements = data?.wasteMeasurements || [];
    const isLoaded = !isLoading;
    useArrayGrowth(wasteMeasurements, isLoaded, scrollToBottom);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [measurementToDelete, setMeasurementToDelete] = useState<string | undefined>(undefined);

    const handleCloseDialog = useCallback(() => {
        setDialogOpen(false);
    }, [setDialogOpen]);

    const handleClickDelete = useCallback(
        (measurementId: string) => {
            setMeasurementToDelete(measurementId);
            setDialogOpen(true);
        },
        [setMeasurementToDelete, setDialogOpen]
    );

    const onDelete = useCallback(() => {
        // deselect if this row was selected
        if (selectedRow?.id === measurementToDelete) {
            setSelectedRow(null);
        }
    }, [selectedRow, setSelectedRow, measurementToDelete]);

    if (isLoading) {
        if (loadingTimeout) {
            return (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" padding={4} height="100%">
                    <Stack spacing={3} alignItems="center" maxWidth={400} textAlign="center">
                        <WifiOffIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                        <Typography variant="h6" color="text.primary">
                            Unable to Load Data
                        </Typography>
                        <Typography color="text.secondary">
                            This data isn&apos;t cached for offline use and we can&apos;t reach the server. Please check your internet
                            connection and try again.
                        </Typography>
                        <Button variant="contained" onClick={() => window.location.reload()}>
                            Retry
                        </Button>
                    </Stack>
                </Box>
            );
        }
        return <LoadingIndicator message="Loading event data..." />;
    }

    if (error) {
        return <Typography>Oh no! {JSON.stringify(error)}</Typography>;
    }

    if (wasteMeasurements.length === 0) {
        return (
            <Box
                sx={{
                    textAlign: 'center',
                    py: 6,
                    color: 'text.secondary',
                }}
            >
                <Typography variant="h6" sx={{ mb: 1 }}>
                    No data yet
                </Typography>
                <Typography variant="body2">Click &quot;Edit Data&quot; to add your first measurement</Typography>
            </Box>
        );
    }

    return (
        <>
            <DeleteMeasurementDialog
                measurementId={measurementToDelete}
                open={dialogOpen}
                onClose={handleCloseDialog}
                onDelete={onDelete}
            />
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {editMode && <TableCell /> /* for delete button*/}
                            <TableCell>Category</TableCell>
                            <TableCell>Material</TableCell>
                            <TableCell>Value</TableCell>
                            <TableCell>Timestamp</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {wasteMeasurements.map((measurement, key) => {
                            const oneMaterial = getOneLink(measurement.material);
                            const oneCategory = getOneLink(oneMaterial?.category);
                            const date = new Date(measurement.timestamp);
                            const dateFormatted = date.toLocaleString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                                month: 'numeric',
                                day: 'numeric',
                                year: '2-digit',
                            });
                            const isSelected = selectedRow?.id === measurement.id;
                            const backgroundColor = isSelected ? '#f0f0f0' : 'inherit';
                            return (
                                <TableRow
                                    key={key}
                                    onClick={() => {
                                        handleClickRow(measurement);
                                    }}
                                    sx={{
                                        cursor: editMode ? 'pointer' : undefined,
                                        backgroundColor,
                                    }}
                                >
                                    {editMode && (
                                        <TableCell>
                                            <IconButton
                                                onClick={ev => {
                                                    ev.stopPropagation();
                                                    handleClickDelete(measurement.id);
                                                }}
                                                edge="end"
                                                aria-label="delete"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                    <TableCell>{oneCategory?.name}</TableCell>
                                    <TableCell>{oneMaterial?.name}</TableCell>
                                    <TableCell>
                                        {measurement.quantity}&nbsp;{measurement.units}
                                    </TableCell>
                                    <TableCell>{dateFormatted}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}
